import {
    AccessLevel,
    ICorporationResponse,
    ICurrentUserResponse,
    ILocationResponse,
} from '@kcftech/prometheus-client';
import { useAccess } from 'contexts/AccessContext';
import { useFeatureFlag } from 'contexts/configcat/useFeatureFlag';
import { usePermissions } from 'contexts/PermissionsContext';
import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import {
    determineCorporationNavigationRoute,
    determineGlobalNavigationRoute,
    determineLocationNavigationRoute,
    FeatureFlags,
    getCorporationLandingPageRoute,
    getHardwareRoute,
} from 'utils/RouteHelper';

export const useScopeNavigation = (user: ICurrentUserResponse | undefined) => {
    const history = useHistory();
    const { pathname } = useLocation();
    const permissions = usePermissions();
    const { canViewCorporationMap } = permissions;
    const { isPremium } = useAccess();
    const { value: myDeskActivity } = useFeatureFlag('myDeskActivity');
    const featureFlags: FeatureFlags = useMemo(
        () => ({ myDeskActivity }),
        [myDeskActivity],
    );

    const corporation = useMemo(() => {
        const determineRoute = (corporation: ICorporationResponse) => {
            // If switching from a Corporation with premium access to a
            // Corporation with basic access, redirect to the new Corporation's
            // Hardware page (default page for Basic Corporations).
            if (corporation.AccessLevel === AccessLevel.Basic) {
                return getHardwareRoute(corporation.Id);
            }

            const url = determineCorporationNavigationRoute(
                pathname,
                corporation.Id,
                true,
                permissions,
                featureFlags,
            );

            if (url.includes('maps') && !canViewCorporationMap) {
                return getCorporationLandingPageRoute(corporation.Id);
            }

            return url;
        };

        const navigateTo = (corporation: ICorporationResponse) => {
            history.push(determineRoute(corporation));
        };

        return { determineRoute, navigateTo };
    }, [canViewCorporationMap, featureFlags, history, pathname, permissions]);

    const location = useMemo(() => {
        const determineRoute = (location: ILocationResponse) => {
            return determineLocationNavigationRoute(pathname, location.Id, isPremium);
        };

        const navigateTo = (location: ILocationResponse) => {
            history.push(determineRoute(location));
        };

        return { determineRoute, navigateTo };
    }, [isPremium, history, pathname]);

    const global = useMemo(() => {
        const determineRoute = () => {
            return user
                ? determineGlobalNavigationRoute(
                      pathname,
                      user,
                      permissions,
                      featureFlags,
                  )
                : '/';
        };

        const navigateTo = () => {
            history.push(determineRoute());
        };

        return { determineRoute, navigateTo };
    }, [featureFlags, history, pathname, user, permissions]);

    return {
        determineCorporationRoute: corporation.determineRoute,
        determineLocationRoute: location.determineRoute,
        determineGlobalRoute: global.determineRoute,
        globalUrl: global.determineRoute(),
        navigateToCorporationPage: corporation.navigateTo,
        navigateToLocationPage: location.navigateTo,
        navigateToGlobalPage: global.navigateTo,
    };
};
